import { Button, Checkbox, Col, Form, Input, Row, Select, Upload } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";
import { Company, Sector } from "interfaces/companies";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";

export interface EditCompanyData {
  name: string;
  secotrs: number[];
  size: number;
  description?: string;
  logo?: File;
  cover_image?: File;
  url?: string;
  organization_number: string;
  visitor_address?: string;
  visitor_area_code?: string;
  visitor_region?: string;
  country?: string;
  telephone?: string;
  email?: string;
}
interface EditCompanyFormProps {
  onSave: (company: Company) => void;
  company: Company;
}

const apiUrl = process.env.REACT_APP_API_URL ?? "";

const EditCompanyForm: React.FC<EditCompanyFormProps> = ({ company, onSave }) => {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [form] = Form.useForm();
  const [logo, setLogo] = useState<File | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState<boolean>(false);
  const [removeCoverImage, setRemoveCoverImage] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => {
      setSectors(response.data);
    });
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      ...company,
    });
    setLogo(null);
    setLogoPreview(null);
    setRemoveLogo(false);
    setCoverImage(null);
    setCoverImagePreview(null);
    setRemoveCoverImage(false);
  }, [form, company]);

  const CheckBoxSectors = sectors.map(sector => {
    if (!sector.name) return null;
    return (
      <Col key={sector.id} lg={4} md={6} sm={8} xs={12}>
        <Checkbox key={sector.id} value={sector.id}>
          {sector.name}
        </Checkbox>
      </Col>
    );
  });

  useEffect(() => {
    if (logo) {
      getBase64(logo)
        .then(url => setLogoPreview(url))
        .catch(() => setLogoPreview(null));
    }
    if (coverImage) {
      getBase64(coverImage)
        .then(url => setCoverImagePreview(url))
        .catch(() => setCoverImagePreview(null));
    }
  }, [logo, coverImage]);

  const onFinish = (formValues: EditCompanyData) => {
    setLoading(true);
    const formData = new FormData();
    if (formValues) {
      Object.entries(formValues).forEach(([key, value]) => {
        if (value instanceof Array) {
          value.forEach(v => formData.append(key, v));
          return;
        }
        formData.append(key, value as string);
      });
    }
    if (logo) formData.append("logo", logo);
    if (removeLogo) formData.append("logo", "");
    if (coverImage) formData.append("logo_wide", coverImage);
    if (removeCoverImage) formData.append("logo_wide", "");

    axios
      .patch<Company>(`/companies/${company.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        onSave(response.data);
        setLoading(false);
        message("success", "Bedriften er oppdatert");
      })
      .catch(error => {
        const errorResponse = ((error as unknown) as { response: { data: { email?: string[] } } }).response?.data;
        errorResponse?.email?.includes("This field must be unique.")
          ? message("error", "Feil under oppdatering", "Epostadressen er allerede i bruk hos en annen bedrift")
          : message("error", "Noe gikk galt under oppdatering av bedriften.");
        setLoading(false);
      });
  };

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} style={{ width: "100%" }}>
        <Form.Item
          label="Bedriftsnavn"
          name="name"
          rules={[
            {
              required: true,
              message: "Skriv inn navn på bedriften",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Bransje(r)"
          name="sectors"
          required
          rules={[
            () => ({
              validator(_, value) {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error("Velg minst én bransje bedriften tilhører"));
                }
                if (value.length > 4) {
                  return Promise.reject(new Error("Velg maks fire bransjer bedriften tilhører"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Checkbox.Group>
            <Row style={{ minHeight: "66px" }}>{CheckBoxSectors}</Row>
          </Checkbox.Group>
        </Form.Item>

        <Row gutter={24}>
          <Col md={12} sm={12} xs={24}>
            <Form.Item
              label="Antall ansatte"
              name="size"
              rules={[
                {
                  required: true,
                  message: "Oppgi antall ansatte",
                },
              ]}
            >
              <Select
                placeholder="Velg antall ansatte"
                options={[
                  { label: "1-10", value: 1 },
                  { label: "11-50", value: 2 },
                  { label: "51-200", value: 3 },
                  { label: "201-500", value: 4 },
                  { label: "501-1000", value: 5 },
                  { label: "1001-5000", value: 6 },
                  { label: "5001-10 000", value: 7 },
                  { label: "10 001+", value: 8 },
                ]}
              />
            </Form.Item>
          </Col>
          <Col md={12} sm={12} xs={24}>
            <Form.Item
              label="Organisasjonsnummer"
              name="organization_number"
              rules={[
                {
                  required: true,
                  message: "Skriv inn organisasjonsnummer",
                },
                {
                  pattern: /^[0-9]{9}$/,
                  message: "Organisasjonsnummer må være 9 siffer langt.",
                },
              ]}
              normalize={(value: string) => value}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col md={8} sm={24} xs={24}>
            <Form.Item label="Nettside" name="url">
              <Input addonBefore="https://" />
            </Form.Item>
          </Col>
          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label="E-postadresse"
              name="email"
              rules={[{ type: "email", message: "Skriv inn en gyldig epostadresse" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col md={8} sm={12} xs={24}>
            <Form.Item label="Telefonnummer" name="telephone">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col md={12} sm={24} xs={24}>
            <Form.Item label="Gateadresse" name="visitor_address">
              <Input />
            </Form.Item>
          </Col>
          <Col md={4} sm={12} xs={24}>
            <Form.Item label="Postnummer" name="visitor_area_code">
              <Input />
            </Form.Item>
          </Col>
          <Col md={4} sm={12} xs={24}>
            <Form.Item label="Poststed" name="visitor_region">
              <Input />
            </Form.Item>
          </Col>
          <Col md={4} sm={12} xs={24}>
            <Form.Item label="Land" name="country">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Beskrivelse" name="description">
          <ReactQuill theme="snow" />
        </Form.Item>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Logo"
              rules={[
                {
                  required: true,
                  message: "Last opp bedriftslogo",
                },
              ]}
            >
              <Upload
                listType="picture-card"
                beforeUpload={file => {
                  setLogo(file);
                  setRemoveLogo(false);
                  // Return false to prevent immediate upload
                  return false;
                }}
                onRemove={() => {
                  setLogo(null);
                  setLogoPreview(null);
                  setRemoveLogo(true);
                }}
                fileList={
                  removeLogo
                    ? []
                    : logo
                    ? logoPreview
                      ? [
                          {
                            uid: "-1",
                            name: "Logo",
                            status: "done",
                            url: logoPreview,
                          },
                        ]
                      : []
                    : company.logo
                    ? [
                        {
                          uid: "-1",
                          name: company.logo?.substring(company.logo?.lastIndexOf("/") + 1),
                          status: "done",
                          url: company.logo?.includes("http") ? company.logo : `${apiUrl}${company.logo}`,
                        },
                      ]
                    : []
                }
              >
                {uploadButton}
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Coverbilde">
              <Upload
                listType="picture-card"
                beforeUpload={file => {
                  setCoverImage(file);
                  setRemoveCoverImage(false);
                  // Return false to prevent immediate upload
                  return false;
                }}
                onRemove={() => {
                  setCoverImage(null);
                  setCoverImagePreview(null);
                  setRemoveCoverImage(true);
                }}
                fileList={
                  removeCoverImage
                    ? []
                    : coverImage
                    ? coverImagePreview
                      ? [
                          {
                            uid: "-1",
                            name: "Coverbilde",
                            status: "done",
                            url: coverImagePreview,
                          },
                        ]
                      : []
                    : company.logo_wide
                    ? [
                        {
                          uid: "-1",
                          name: company.logo_wide.substring(company.logo_wide?.lastIndexOf("/") + 1),
                          status: "done",
                          url: company.logo_wide.includes("http")
                            ? company.logo_wide
                            : `${apiUrl}/${company.logo_wide}`,
                        },
                      ]
                    : []
                }
              >
                {uploadButton}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button type="primary" size="large" loading={loading} htmlType="submit" style={{ width: "100%" }}>
            Oppdater bedrift
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default EditCompanyForm;
